/**
 * QuizReview Component
 *
 * Review screen shown before submitting quiz.
 * Displays all questions with selected answers.
 * Highlights unanswered questions with warning.
 * Allows navigation back to specific questions.
 */

import type { QuizQuestion } from '../lib/types';

interface QuizReviewProps {
  questions: QuizQuestion[];
  answers: Map<string, number>;
  onGoToQuestion: (index: number) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

export function QuizReview({
  questions,
  answers,
  onGoToQuestion,
  onSubmit,
  isSubmitting,
}: QuizReviewProps) {
  const answeredCount = answers.size;
  const totalCount = questions.length;
  const unansweredCount = totalCount - answeredCount;

  return (
    <div className="quiz-review">
      <h2 className="quiz-review-title">Review Your Answers</h2>

      <div className="quiz-review-summary">
        <span className="quiz-review-count">
          {answeredCount} of {totalCount} questions answered
        </span>
        {unansweredCount > 0 && (
          <span className="quiz-review-warning">
            {unansweredCount} unanswered
          </span>
        )}
      </div>

      <div className="quiz-review-list">
        {questions.map((question, index) => {
          const selectedIndex = answers.get(question.id);
          const isAnswered = selectedIndex !== undefined;
          const selectedOption = isAnswered
            ? question.options.find((o) => o.index === selectedIndex)
            : null;
          const letterIndex = isAnswered && selectedOption
            ? question.options
                .sort((a, b) => a.index - b.index)
                .findIndex((o) => o.index === selectedIndex)
            : -1;

          return (
            <div
              key={question.id}
              className={`quiz-review-item ${isAnswered ? 'answered' : 'unanswered'}`}
            >
              <div className="quiz-review-item-header">
                <span className="quiz-review-item-number">Q{index + 1}</span>
                <button
                  className="quiz-review-item-link"
                  onClick={() => onGoToQuestion(index)}
                  type="button"
                >
                  {isAnswered ? 'Change' : 'Answer'}
                </button>
              </div>

              <div className="quiz-review-item-question">
                {question.questionText.length > 100
                  ? `${question.questionText.slice(0, 100)}...`
                  : question.questionText}
              </div>

              {isAnswered && selectedOption ? (
                <div className="quiz-review-item-answer">
                  <span className="quiz-review-item-letter">
                    {OPTION_LETTERS[letterIndex]}
                  </span>
                  <span className="quiz-review-item-text">
                    {selectedOption.text.length > 50
                      ? `${selectedOption.text.slice(0, 50)}...`
                      : selectedOption.text}
                  </span>
                </div>
              ) : (
                <div className="quiz-review-item-unanswered">
                  Not answered
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="quiz-review-actions">
        <button
          className="btn btn-primary quiz-submit-btn"
          onClick={onSubmit}
          disabled={isSubmitting || answeredCount === 0}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Quiz'}
        </button>
        {answeredCount === 0 && (
          <p className="quiz-review-error">
            Please answer at least one question before submitting.
          </p>
        )}
      </div>
    </div>
  );
}
